import chroma from "chroma-js";
import { produce } from "immer";
import isEmpty from "lodash/isEmpty";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  Color,
  CustomFont,
  ErrorResponse,
  Font,
  FontImport,
  Page,
  Theme,
  ThemeKind,
} from "src/types";

import useEvent from "@/hooks/use-event";
import useFontLoader, { getFontsByProvider } from "@/hooks/use-font-loader";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch } from "@/utils/fetchJSON";
import { isError } from "@/utils/is-error";

import { CanvasThemesProviderContext } from "../canvas-themes-provider/canvas-themes-provider-component";
import {
  isCustomFont,
  isFontImport,
} from "../editor/preference-panel/global-fonts/utils";
import { StyledThemeProvider } from "../styled-theme-provider/styled-theme-provider-component";

const get = endpoints();

export type TPageProviderContext = {
  updateFont: (font: Font | CustomFont, index: number) => void;
  updateColors: (colors: Color[]) => void;
  updateTheme: (kind: ThemeKind) => void;
  page: Page;
  setPage: (cb: (page: Page) => void) => void;
};

type PageProviderProps = {
  children: React.ReactNode;
  page: Page;
};

export type PageTheme = {
  kind: ThemeKind;
};

export const PageProviderContext = createContext<TPageProviderContext>(
  {} as TPageProviderContext
);

// For backward compatibility.
// We used to have fonts mapped as an object with `primary`, `secondary`, `tertiary`
// keys. Currently they are in a list.
export function parseFonts(fonts) {
  if (Array.isArray(fonts)) {
    return fonts;
  }

  return Object.keys(fonts).map((key) => {
    const font = fonts[key];
    return {
      // Server validation makes this `name` required.. but it's not.
      name: " ",
      ...font,
    };
  });
}

const getDefaultProperties = (themes: Theme[]) => (
  properties: Partial<Page["properties"]> = {}
): Page["properties"] => {
  let themeKind = properties.theme?.kind;
  if (isEmpty(properties)) {
    //newly created page
    themeKind = ThemeKind.CROSBY;
  } else {
    // Legacy event that is missing properties.theme.kind
    themeKind = themeKind || ThemeKind.DEFAULT;
  }
  let theme = themes.find((theme) => theme.kind === themeKind);

  // Edge case:
  // Load default theme if we can't resolve the saved theme
  if (!theme) {
    theme = themes.find((theme) => theme.kind === ThemeKind.CROSBY);
  }

  const fonts = parseFonts(properties.fonts || []);
  const colors = properties.colors || [];

  return {
    theme: {
      kind: themeKind,
    },
    fonts: fonts.length > 0 ? fonts : theme.fonts || [],
    colors:
      colors.length > 0
        ? colors
        : theme.colorSet[0]?.colors.map(
            (color) => chroma.valid(color) && chroma(color).hex()
          ) || [],
  };
};

export default function PageProvider({
  children,
  page: savedPage,
}: PageProviderProps) {
  const { themes } = useContext(CanvasThemesProviderContext);
  const themesRef = useRef(themes);
  themesRef.current = themes;

  const [page, internalSetPage] = useState({
    ...savedPage,
    properties: getDefaultProperties(themes)(savedPage.properties),
  });

  const setPage = useCallback((mutator: (draft: typeof page) => void) => {
    internalSetPage(
      produce((page) => {
        mutator(page);
      })
    );
  }, []);

  const updateTheme = useCallback(
    (kind: ThemeKind) => {
      // Since updateTheme is part of the context value -
      // Rather than adding `themes` as a dep which will recreate the updateTheme callback everytime `themes` gets updated
      // (ie: if a feature flag gets turned on/off)
      // Instead, we can just refer to themes via its mutable ref
      const { current: themes } = themesRef;

      const theme = themes.find((theme) => theme.kind === kind);
      if (!theme) {
        return;
      }

      setPage((page) => {
        if (page.properties.theme.kind === kind) {
          return;
        }

        page.properties.colors = theme.colorSet[0].colors;
        page.properties.fonts = theme.fonts;
        page.properties.theme = {
          kind,
        };
      });
    },
    [setPage]
  );

  const { event } = useEvent();

  useFontLoader({
    families: getFontsByProvider(page.properties.fonts, "google"),
    type: "Google",
  });

  useFontLoader({
    families: page.font_imports.map((fontImport) => fontImport.font.name),
    pageId: page.id,
    type: "Custom",
  });

  const apiRemoveFontImport = useCallback(
    (fontImport: FontImport) => {
      return new Promise((resolve) => {
        return authenticatedFetch
          .fetchJSON(
            get("FONT_IMPORT_API")({
              slug: event.slug,
              pageId: page.id.toString(),
              fontImportId: fontImport.id.toString(),
            }),
            {
              method: "DELETE",
            }
          )
          .catch(() => {
            // ignore. we use authenticatedFetch for ease of the attached token;
            // however, the delete endpoint currently returns nothing on a
            // successful delete request, which throws when we try to response.json().
          })
          .finally(() => resolve(true));
      });
    },
    [event.slug, page.id]
  );

  const apiCreateFontImport = useCallback(
    async (customFont: CustomFont): Promise<[FontImport, ErrorResponse]> => {
      // create a font import
      const response = await authenticatedFetch.fetchJSON<FontImport>(
        get("FONT_IMPORT_API")({
          slug: event.slug,
          pageId: page.id.toString(),
        }),
        {
          method: "POST",
          body: {
            font_import: {
              font_id: customFont.id,
            },
          },
        }
      );

      let error = null;
      let fontImport: FontImport = null;

      if (isError(response)) {
        error = response;
      } else {
        fontImport = response;
      }

      return [fontImport, error];
    },
    [event.slug, page.id]
  );

  const updateFont = useCallback(
    async (font: Font | CustomFont, index: number) => {
      const primaryFont = page.font_imports[0] || page.properties.fonts[0];

      if (isFontImport(primaryFont)) {
        const resp = await apiRemoveFontImport(primaryFont);
        if (isError(resp)) {
          toast.error(resp.description);
          return;
        }
      }

      if (isCustomFont(font)) {
        const [fontImport, error] = await apiCreateFontImport(font);

        if (error) {
          toast.error(error.description);
          return;
        }

        setPage((page) => {
          page.font_imports[index] = fontImport;
        });
      } else {
        setPage((page) => {
          page.font_imports.splice(0);
          page.properties.fonts[index] = font;
        });
      }
    },
    [
      apiCreateFontImport,
      apiRemoveFontImport,
      page.font_imports,
      page.properties.fonts,
      setPage,
    ]
  );

  const updateColors = useCallback(
    (colors: Color[]) => {
      setPage((page) => {
        page.properties.colors = colors;
      });
    },
    [setPage]
  );

  const contextValue = useMemo(
    () => ({
      page,
      setPage,
      updateFont,
      updateColors,
      updateTheme,
    }),
    [page, setPage, updateFont, updateColors, updateTheme]
  );

  return (
    <PageProviderContext.Provider value={contextValue}>
      <StyledThemeProvider>{children}</StyledThemeProvider>
    </PageProviderContext.Provider>
  );
}
